/* StoneLink — GPU fluid gradient-mesh background.
   Domain-warped fractal noise → palette ramp, slow drift, soft cursor light,
   and click ripples that wash across the colors. No video, no image assets.
   Exposes window.StoneBG: { setPalette, setSpeed, setIntensity, setGrain,
   setMouseInfluence, addRipple, ready }.  */
(function () {
  "use strict";

  const VERT = `
    attribute vec2 aPos;
    varying vec2 vUv;
    void main() {
      vUv = aPos * 0.5 + 0.5;
      gl_Position = vec4(aPos, 0.0, 1.0);
    }
  `;

  const FRAG = `
    precision highp float;
    varying vec2 vUv;
    uniform vec2  uRes;
    uniform float uTime;
    uniform vec2  uMouse;
    uniform float uMouseInfluence;
    uniform float uSpeed;
    uniform float uIntensity;
    uniform float uGrain;
    uniform vec3  uC0;
    uniform vec3  uC1;
    uniform vec3  uC2;
    uniform vec3  uC3;
    uniform vec3  uBg;
    #define MAX_RIPPLES 10
    uniform vec3  uRipples[MAX_RIPPLES]; // xy = uv origin, z = start time (s); z<0 = inactive

    float hash(vec2 p){
      p = fract(p * vec2(123.34, 456.21));
      p += dot(p, p + 45.32);
      return fract(p.x * p.y);
    }
    float noise(vec2 p){
      vec2 i = floor(p);
      vec2 f = fract(p);
      float a = hash(i);
      float b = hash(i + vec2(1.0, 0.0));
      float c = hash(i + vec2(0.0, 1.0));
      float d = hash(i + vec2(1.0, 1.0));
      vec2 u = f * f * (3.0 - 2.0 * f);
      return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
    }
    float fbm(vec2 p){
      float v = 0.0;
      float a = 0.5;
      for (int i = 0; i < 6; i++) {
        v += a * noise(p);
        p = p * 2.0 + vec2(11.3, 7.7);
        a *= 0.5;
      }
      return v;
    }

    void main(){
      vec2 uv = vUv;
      float aspect = uRes.x / uRes.y;
      vec2 ap = vec2(uv.x * aspect, uv.y);
      float t = uTime * 0.07 * uSpeed;

      // domain-warped flow field
      vec2 q = vec2(
        fbm(ap * 1.5 + vec2(0.0, t)),
        fbm(ap * 1.5 + vec2(5.2, 1.3) - t)
      );
      vec2 r = vec2(
        fbm(ap * 1.5 + 1.9 * q + vec2(1.7, 9.2) + t * 0.7),
        fbm(ap * 1.5 + 1.9 * q + vec2(8.3, 2.8) - t * 0.6)
      );
      float f = fbm(ap * 1.5 + 2.8 * r);
      // expand dynamic range so the brighter palette stops actually bloom
      f = smoothstep(0.20, 0.78, f);

      // click ripples wash across the field
      float ripple = 0.0;
      for (int i = 0; i < MAX_RIPPLES; i++) {
        vec3 rp = uRipples[i];
        if (rp.z < 0.0) continue;
        float age = uTime - rp.z;
        if (age < 0.0 || age > 4.5) continue;
        vec2 d = vec2((uv.x - rp.x) * aspect, uv.y - rp.y);
        float dist = length(d);
        float wave = sin(dist * 20.0 - age * 6.5);
        float env = exp(-dist * 3.2) * exp(-age * 1.5) * smoothstep(0.0, 0.12, age);
        ripple += wave * env;
      }
      f += ripple * 0.24;
      f = clamp(f, 0.0, 1.0);

      // palette ramp across four stops
      vec3 col = mix(uC0, uC1, smoothstep(0.00, 0.32, f));
      col = mix(col, uC2, smoothstep(0.28, 0.64, f));
      col = mix(col, uC3, smoothstep(0.58, 0.96, f));

      // richness modulation from the warp
      col = mix(col, uC2, (r.x - 0.5) * 0.22);

      // slow-orbiting aurora glows — guaranteed living, drifting color
      vec2 g1 = vec2(0.62 + 0.30 * sin(uTime * 0.061), 0.58 + 0.20 * cos(uTime * 0.047));
      vec2 g2 = vec2(0.30 + 0.26 * cos(uTime * 0.041 + 1.7), 0.42 + 0.24 * sin(uTime * 0.055 + 0.6));
      float gd1 = exp(-dot((uv - g1) * vec2(aspect, 1.0), (uv - g1) * vec2(aspect, 1.0)) * 2.0);
      float gd2 = exp(-dot((uv - g2) * vec2(aspect, 1.0), (uv - g2) * vec2(aspect, 1.0)) * 2.6);
      col += uC3 * gd1 * 0.34 * uIntensity;
      col += uC2 * gd2 * 0.30 * uIntensity;

      // soft cursor light
      vec2 md = vec2((uv.x - uMouse.x) * aspect, uv.y - uMouse.y);
      float ml = exp(-dot(md, md) * 5.5);
      col += ml * 0.10 * uMouseInfluence * (0.6 + 0.4 * sin(uTime * 1.5));

      // intensity toward background base
      col = mix(uBg, col, clamp(uIntensity, 0.0, 1.6));

      // vignette
      float vig = smoothstep(1.3, 0.28, length(uv - 0.5));
      col *= mix(0.86, 1.0, vig);

      // fine grain to break up banding
      float g = (hash(uv * uRes + fract(uTime)) - 0.5) * uGrain;
      col += g;

      gl_FragColor = vec4(col, 1.0);
    }
  `;

  function compile(gl, type, src) {
    const sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      console.error("StoneBG shader error:", gl.getShaderInfoLog(sh));
      gl.deleteShader(sh);
      return null;
    }
    return sh;
  }

  function hexToRGB(hex) {
    hex = hex.replace("#", "");
    if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
    const n = parseInt(hex, 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }
  // perceptual-ish ease so palette swaps don't look flat
  function srgb(c) { return c; }

  const canvas = document.getElementById("bg-canvas");
  const gl =
    canvas &&
    (canvas.getContext("webgl", { antialias: false, alpha: false, premultipliedAlpha: false }) ||
      canvas.getContext("experimental-webgl"));

  // state
  const state = {
    c0: hexToRGB("#10151f"),
    c1: hexToRGB("#2c3e5c"),
    c2: hexToRGB("#5b7fa6"),
    c3: hexToRGB("#bcd6ea"),
    bg: hexToRGB("#080b12"),
    speed: 1.0,
    intensity: 0.5,
    grain: 0.04,
    mouseInfluence: 1.0,
  };
  // animated targets for smooth palette transitions
  const target = JSON.parse(JSON.stringify(state));

  const ripples = new Float32Array(30).fill(-1); // 10 × vec3
  let rippleHead = 0;

  const mouse = { x: 0.5, y: 0.5, tx: 0.5, ty: 0.5 };

  // public API — works even if WebGL fails (no-ops)
  const API = {
    ready: !!gl,
    setPalette(p) {
      if (p.c0) target.c0 = hexToRGB(p.c0);
      if (p.c1) target.c1 = hexToRGB(p.c1);
      if (p.c2) target.c2 = hexToRGB(p.c2);
      if (p.c3) target.c3 = hexToRGB(p.c3);
      if (p.bg) target.bg = hexToRGB(p.bg);
    },
    setSpeed(v) { target.speed = v; },
    setIntensity(v) { target.intensity = v; },
    setGrain(v) { target.grain = v; },
    setMouseInfluence(v) { target.mouseInfluence = v; },
    addRipple(nx, ny) {
      const i = rippleHead * 3;
      ripples[i] = nx;
      ripples[i + 1] = ny;
      ripples[i + 2] = (performance.now() - t0) / 1000;
      rippleHead = (rippleHead + 1) % 10;
    },
    setMouse(nx, ny) { mouse.tx = nx; mouse.ty = ny; },
    debugSample() {
      if (!gl) return { ready: false };
      const px = new Uint8Array(4);
      gl.readPixels(
        Math.floor(canvas.width * 0.5),
        Math.floor(canvas.height * 0.5),
        1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px
      );
      return { ready: true, center: [px[0], px[1], px[2]], w: canvas.width, h: canvas.height };
    },
  };
  window.StoneBG = API;

  if (!gl) {
    if (canvas) canvas.style.background =
      "radial-gradient(120% 120% at 30% 20%, #2c3e5c, #10151f 55%, #080b12)";
    return;
  }

  const vs = compile(gl, gl.VERTEX_SHADER, VERT);
  const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
  const prog = gl.createProgram();
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    console.error("StoneBG link error:", gl.getProgramInfoLog(prog));
    return;
  }
  gl.useProgram(prog);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 3, -1, -1, 3]),
    gl.STATIC_DRAW
  );
  const aPos = gl.getAttribLocation(prog, "aPos");
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

  const U = {};
  ["uRes", "uTime", "uMouse", "uMouseInfluence", "uSpeed", "uIntensity",
   "uGrain", "uC0", "uC1", "uC2", "uC3", "uBg", "uRipples[0]"].forEach((n) => {
    U[n] = gl.getUniformLocation(prog, n);
  });

  let dpr = Math.min(window.devicePixelRatio || 1, 1.6);
  function resize() {
    dpr = Math.min(window.devicePixelRatio || 1, 1.6);
    const w = Math.floor(window.innerWidth * dpr);
    const h = Math.floor(window.innerHeight * dpr);
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    canvas.style.width = window.innerWidth + "px";
    canvas.style.height = window.innerHeight + "px";
    gl.viewport(0, 0, w, h);
  }
  window.addEventListener("resize", resize);
  resize();

  const t0 = performance.now();
  let last = t0;

  function lerp(a, b, k) { return a + (b - a) * k; }
  function lerp3(a, b, k) { return [lerp(a[0], b[0], k), lerp(a[1], b[1], k), lerp(a[2], b[2], k)]; }

  function frame(now) {
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;
    const k = 1 - Math.pow(0.001, dt); // smoothing

    // ease state toward targets
    state.c0 = lerp3(state.c0, target.c0, k * 1.2);
    state.c1 = lerp3(state.c1, target.c1, k * 1.2);
    state.c2 = lerp3(state.c2, target.c2, k * 1.2);
    state.c3 = lerp3(state.c3, target.c3, k * 1.2);
    state.bg = lerp3(state.bg, target.bg, k * 1.2);
    state.speed = lerp(state.speed, target.speed, k);
    state.intensity = lerp(state.intensity, target.intensity, k);
    state.grain = lerp(state.grain, target.grain, k);
    state.mouseInfluence = lerp(state.mouseInfluence, target.mouseInfluence, k);

    mouse.x = lerp(mouse.x, mouse.tx, k * 0.9);
    mouse.y = lerp(mouse.y, mouse.ty, k * 0.9);

    gl.uniform2f(U.uRes, canvas.width, canvas.height);
    gl.uniform1f(U.uTime, (now - t0) / 1000);
    gl.uniform2f(U.uMouse, mouse.x, mouse.y);
    gl.uniform1f(U.uMouseInfluence, state.mouseInfluence);
    gl.uniform1f(U.uSpeed, state.speed);
    gl.uniform1f(U.uIntensity, state.intensity);
    gl.uniform1f(U.uGrain, state.grain);
    gl.uniform3fv(U.uC0, state.c0);
    gl.uniform3fv(U.uC1, state.c1);
    gl.uniform3fv(U.uC2, state.c2);
    gl.uniform3fv(U.uC3, state.c3);
    gl.uniform3fv(U.uBg, state.bg);
    gl.uniform3fv(U["uRipples[0]"], ripples);

    gl.drawArrays(gl.TRIANGLES, 0, 3);
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
})();
